import {
  useV2GetProjectByIdQuery,
  V2GetProjectByIdApiArg,
  V2GetProjectByIdApiResponse,
} from "@generated";
import { Project } from "../types/Project";

// Top-level names are all v2 ProjectResource relationships; the dotted nested
// names are relationships on the corresponding v2 child resources, which the
// generated include union does not list.
const PROJECT_INCLUDES = [
  /* PROJECT INFO (GENERAL) */
  "payment-term",
  "rate-table",
  "project-contacts",
  "project-conditions",
  "project-collaborators",
  "project-attachments",
  "notes",

  /* PROJECT SERVICES */
  "project-services",
  "project-services.service",
  "project-services.project-phase",
  "project-services.project-resource",

  /* PROJECT PRODUCTS */
  "project-products",
  "project-products.product",
  "project-products.project-service",

  /* PROJECT GOVERNANCE */
  "project-governances",
  "project-governances.governance",
  "project-governances.project-resource",
  "project-governances.project-phase",

  /* PROJECT RESOURCES */
  "project-resources",
  "project-resources.line-of-business",

  /* PROJECT EXPENSE */
  "project-expenses",
  "project-expenses.expense-category",
  "project-expenses.project-phase",

  /* QUOTES */
  "quotes",
  "quotes.vendor",

  /* PRICING */
  "project-credits",
] as unknown as V2GetProjectByIdApiArg["include"];

type ResourceRef = { type: string; id: string };

// Nested includes bring back record types (services, vendors,
// line-of-businesses, ...) that the generated `included` union does not list,
// so records are read through this loose shape.
interface JsonApiRecord {
  id?: string;
  type: string;
  attributes?: Record<string, any>;
  relationships?: Record<string, { data?: ResourceRef | ResourceRef[] | null }>;
}

const cleanResource = (raw: JsonApiRecord, relationshipKeys: string[]) => {
  const result: Record<string, any> = {
    id: raw?.id,
    type: raw?.type,
    attributes: raw?.attributes,
  };
  if (relationshipKeys.length > 0) {
    const relationships: Record<string, any> = {};
    for (const key of relationshipKeys) {
      const rel = raw?.relationships?.[key];
      if (rel !== undefined) {
        relationships[key] = { data: rel?.data ?? null };
      }
    }
    result.relationships = relationships;
  }
  return result;
};

const toOneId = (
  record: JsonApiRecord | undefined,
  key: string
): string | undefined => {
  const data = record?.relationships?.[key]?.data;
  if (!data || Array.isArray(data) || data.id == null) return undefined;
  return String(data.id);
};

const toManyRefs = (
  record: JsonApiRecord | undefined,
  key: string
): ResourceRef[] => {
  const data = record?.relationships?.[key]?.data;
  return Array.isArray(data) ? data : [];
};

/**
 * Normalizes a v2 GET /v2/projects/{id} response (with PROJECT_INCLUDES) into
 * the shape both the Create Blueprint and Duplicate Project modals consume.
 * Throws when the project record is missing or malformed.
 */
export const transformProject = (
  response: V2GetProjectByIdApiResponse
): Project => {
  const p = response?.data as JsonApiRecord | undefined;
  if (!p?.id || !p?.relationships) {
    throw new Error("Project data is missing or malformed");
  }

  const included = (response?.included ?? []) as JsonApiRecord[];
  const byKey = new Map<string, JsonApiRecord>();
  for (const item of included) {
    if (item?.type && item?.id != null) {
      byKey.set(`${item.type}:${item.id}`, item);
    }
  }
  const find = (type: string, id: string | undefined) =>
    id == null ? undefined : byKey.get(`${type}:${id}`);
  const related = (
    record: JsonApiRecord | undefined,
    key: string,
    type: string
  ) => find(type, toOneId(record, key));

  const services = toManyRefs(p, "project-services").flatMap((ref) => {
    const service = find("project-services", ref.id);
    const attrs = service?.attributes;
    if (!service || !attrs?.active) return [];
    const {
      name,
      quantity,
      "service-type": serviceType,
      "total-hours": totalHours,
      "calculated-pricing": calculatedPricing,
    } = attrs;
    const resourceAttrs = related(
      service,
      "project-resource",
      "project-resources"
    )?.attributes;
    return [
      {
        id: String(service.id || ""),
        serviceType: serviceType || "",
        name: String(name || ""),
        selected: true,
        quantity,
        hours: totalHours,
        // calculated-pricing is a Hash attribute; JR does not dasherize
        // the keys inside it.
        price: calculatedPricing?.["service_revenue"],
        cost: calculatedPricing?.["service_cost"],
        resource: resourceAttrs?.["extended-name"] || resourceAttrs?.name || "",
        rawResource: cleanResource(service, [
          "service",
          "project-phase",
          "project-resource",
        ]),
      },
    ];
  });

  const quotes = toManyRefs(p, "quotes").flatMap((ref) => {
    const quote = find("quotes", ref.id);
    const attrs = quote?.attributes;
    if (!quote || !attrs?.active) return [];
    const {
      cost,
      price,
      "recurring-cost": recurringCost,
      "recurring-price": recurringPrice,
    } = attrs;
    const vendor = related(quote, "vendor", "vendors");
    return [
      {
        id: String(quote.id || ""),
        name: String(vendor?.attributes?.name || ""),
        selected: true,
        cost,
        recCost: recurringCost,
        revenue: price,
        recRevenue: recurringPrice,
        rawResource: cleanResource(quote, []),
      },
    ];
  });

  const products = toManyRefs(p, "project-products").flatMap((ref) => {
    const product = find("project-products", ref.id);
    const attrs = product?.attributes;
    if (!product || !attrs?.active) return [];
    const {
      name,
      quantity,
      "manufacturer-part-number": mfrPart,
      "unit-of-measure": units,
      "unit-price": unitPrice,
    } = attrs;
    return [
      {
        id: String(product.id || ""),
        name: String(name || ""),
        selected: true,
        quantity,
        mfrPart,
        units,
        price: unitPrice,
        rawResource: cleanResource(product, ["product", "project-service"]),
      },
    ];
  });

  const governances = toManyRefs(p, "project-governances").flatMap((ref) => {
    const governance = find("project-governances", ref.id);
    const attrs = governance?.attributes;
    if (!governance || !attrs?.active) return [];
    const { description, hours, "calculation-type": calculationType } = attrs;
    const resource = related(
      governance,
      "project-resource",
      "project-resources"
    );
    const phase = related(governance, "project-phase", "project-phases");
    return [
      {
        id: String(governance.id || ""),
        name: String(description || ""),
        selected: true,
        resource: resource?.attributes?.name || "",
        phase: phase?.attributes?.name || "",
        calculation: calculationType || "",
        hours,
        rawResource: cleanResource(governance, [
          "governance",
          "project-resource",
          "project-phase",
        ]),
      },
    ];
  });

  const resources = toManyRefs(p, "project-resources").flatMap((ref) => {
    const resource = find("project-resources", ref.id);
    const attrs = resource?.attributes;
    if (!resource || !attrs?.active) return [];
    const {
      name,
      "hourly-rate": hourlyRate,
      "expense-rate": expenseRate,
    } = attrs;
    const lob = related(resource, "line-of-business", "line-of-businesses");
    return [
      {
        id: String(resource.id || ""),
        name: String(name || ""),
        lob: lob?.attributes?.["nested-name"] || "",
        hourlyRate,
        expenseRate,
        selected: true,
        rawResource: cleanResource(resource, []),
      },
    ];
  });

  const expenses = toManyRefs(p, "project-expenses").flatMap((ref) => {
    const expense = find("project-expenses", ref.id);
    const attrs = expense?.attributes;
    if (!expense || !attrs?.active) return [];
    const { quantity, cost } = attrs;
    const expenseCategory = related(
      expense,
      "expense-category",
      "expense-categories"
    );
    return [
      {
        id: String(expense.id || ""),
        name: String(expenseCategory?.attributes?.name || ""),
        selected: true,
        quantity,
        cost,
        rawResource: cleanResource(expense, [
          "expense-category",
          "project-phase",
        ]),
      },
    ];
  });

  const {
    "executive-summary": executiveSummary,
    "solution-summary": solutionSummary,
    "ps-revenue-adjustment": psRevenueAdjustment,
    "ms-revenue-adjustment": msRevenueAdjustment,
  } = p.attributes ?? {};

  return {
    id: p.id,
    executiveSummary: executiveSummary || "",
    solutionSummary: solutionSummary || "",
    paymentTermId: toOneId(p, "payment-term"),
    rateTableId: toOneId(p, "rate-table"),
    projectContacts: toManyRefs(p, "project-contacts"),
    projectConditions: toManyRefs(p, "project-conditions"),
    projectCollaborators: toManyRefs(p, "project-collaborators"),
    projectAttachments: toManyRefs(p, "project-attachments"),
    projectNotes: toManyRefs(p, "notes"),
    psRevenueAdjustment,
    msRevenueAdjustment,
    projectCredits: toManyRefs(p, "project-credits"),
    services,
    quotes,
    products,
    governances,
    resources,
    expenses,
  };
};

const useGetProject = (id: string) => {
  let customError: string | null = null;

  // The modals that read this mount only while open; refetch on each mount so
  // a reopen doesn't seed its sections from a cached, pre-edit project.
  const { data, isLoading, isFetching, error } = useV2GetProjectByIdQuery(
    { id: String(id), include: PROJECT_INCLUDES },
    { skip: !id, refetchOnMountOrArgChange: true }
  );

  let project: Project | undefined = undefined;

  if (data?.data) {
    try {
      project = transformProject(data);
    } catch (err) {
      customError =
        err instanceof Error ? err.message : "An unknown error occurred";
    }
  }

  return {
    project,
    isLoading,
    // True during a background refetch too; gate pre-fills on this so they
    // run against the fresh response rather than the cached one.
    isFetching,
    error: error ?? customError,
  };
};
export default useGetProject;
